// LedRule.Condition requires complex handling due to arbitrary nesting of boolean logic.
// This is especially complicated for And/Or. These types contain a list of conditions,
// but for easier handling we treat the list in functional way, i.e. by splitting it into
// head and tail and evaluating tail in recursive calls.
// This module contains utility functions for dealing with logic rule conditions.
import { assertUnreachable } from "@/utils";
import { Condition } from "@/keyboard";
import {
  ConditionLogic,
  ConditionTypeLogic,
  isCondNot,
  isCondAnd,
  isCondOr,
  CondAnd,
  CondOr,
} from "@/keyboard/leds";

export const isOther = (a: CondAnd | CondOr, b: Condition): boolean =>
  (isCondAnd(a) && isCondOr(b)) || (isCondOr(a) && isCondAnd(b));

export const cleanup = (v: CondAnd | CondOr): Condition | undefined => {
  const unwrapped = unwrap(v);
  const len = unwrapped.length;
  return len == 0 ? undefined : len == 1 ? unwrapped[0] : v;
};

export const wrap = (v: CondAnd | CondOr, c: Condition[]): CondAnd | CondOr =>
  isCondAnd(v) ? { And: c } : isCondOr(v) ? { Or: c } : assertUnreachable(v);

export const unwrap = (c: CondAnd | CondOr): Condition[] =>
  isCondAnd(c) ? c.And : isCondOr(c) ? c.Or : assertUnreachable(c);

export const merge = (v: CondAnd | CondOr, c?: Condition): CondAnd | CondOr =>
  c == undefined
    ? v
    : isCondAnd(v) && isCondAnd(c)
    ? { And: [...v.And, ...c.And] }
    : isCondOr(v) && isCondOr(c)
    ? { Or: [...v.Or, ...c.Or] }
    : isCondAnd(v)
    ? { And: [...v.And, c] }
    : isCondOr(v)
    ? { Or: [...v.Or, c] }
    : assertUnreachable(v);

export const swap = (v: CondAnd | CondOr): CondAnd | CondOr =>
  isCondAnd(v) ? { Or: v.And } : { And: v.Or };

export const inner = (c: ConditionLogic): Condition[] =>
  isCondNot(c) ? [c.Not] : unwrap(c);

export const outerParens = (
  ctype: ConditionTypeLogic,
  parentLogic?: ConditionTypeLogic
): boolean => !!(parentLogic && parentLogic != ctype);
